/**
 * Reserved handles — names that can't be claimed via the normal signup or
 * ride-handle flow. Three buckets:
 *
 *   1. System / route names — would collide with app routes or admin paths.
 *   2. Brand names — automotive, motorcycle, powersport. Held back so we
 *      can later grant them to verified manufacturers / dealer groups
 *      (potential commercial deals — see plan 15 §5).
 *   3. Popular model names — last ~10 model years. Same rationale as
 *      brands: keep them out of FCFS so we can grant them deliberately.
 *
 * Notes:
 * - All entries are lowercase. The validators normalize input before
 *   checking.
 * - Handles 1–2 chars long are already invalid via charset/length rules,
 *   so we don't list things like "m3", "z", "rs". Three-char-and-up only.
 * - Where a model name is commonly written with a hyphen ("f-150",
 *   "model-3"), we list both forms. Underscored variants aren't listed
 *   because user handle charset already excludes hyphens, and most
 *   model names don't have a "natural" underscored spelling.
 * - To extend: add to the list, run tests. Admin force-claim path
 *   (plan 15 task #6) will let us grant any reserved handle later.
 */

const RESERVED_LIST: readonly string[] = [
  // ── System / route names ──────────────────────────────────────
  // Single-letter entries ("r", "u") are below the min-length cutoff for
  // valid handles, but we keep them here so the source-of-truth includes
  // every name we never want to grant — defence-in-depth if validation
  // ever changes.
  "admin", "api", "app", "auth", "billing", "blog", "contact", "dashboard",
  "docs", "feed", "garage", "garages", "garageregister", "help", "home",
  "login", "marketing", "mobile", "notifications", "null", "pricing",
  "privacy", "profile", "r", "ride", "rides", "rvhd", "revhead",
  "revheadpro", "revhead-pro", "search", "settings", "signin", "signup",
  "signout", "support", "system", "team", "terms", "test", "tests", "u",
  "undefined", "user", "users", "vehicle", "vehicles", "web",

  // ── Brands — automotive ────────────────────────────────────────
  "acura", "alfa-romeo", "alfaromeo", "aston-martin", "astonmartin", "audi",
  "bentley", "bmw", "bugatti", "buick", "cadillac", "chevrolet", "chevy",
  "chrysler", "citroen", "corvette", "dodge", "ferrari", "fiat", "ford",
  "genesis", "gmc", "honda", "hyundai", "infiniti", "jaguar", "jeep", "kia",
  "koenigsegg", "lamborghini", "lancia", "land-rover", "landrover", "lexus",
  "lincoln", "lotus", "lucid", "maserati", "maybach", "mazda", "mclaren",
  "mercedes", "mercedes-benz", "mercedesbenz", "mini", "mitsubishi", "nissan",
  "pagani", "peugeot", "polestar", "pontiac", "porsche", "ram", "renault",
  "rivian", "rolls-royce", "rollsroyce", "saab", "scion", "smart", "subaru",
  "suzuki", "tesla", "toyota", "vauxhall", "volkswagen", "volvo", "vw",

  // ── Brands — motorcycle / powersport ──────────────────────────
  "aprilia", "benelli", "beta", "buell", "can-am", "canam", "cfmoto",
  "cf-moto", "ducati", "gas-gas", "gasgas", "harley", "harley-davidson",
  "harleydavidson", "husaberg", "husqvarna", "indian", "kawasaki", "ktm",
  "moto-guzzi", "motoguzzi", "mv-agusta", "mvagusta", "norton", "piaggio",
  "polaris", "royal-enfield", "royalenfield", "sea-doo", "seadoo", "ski-doo",
  "skidoo", "triumph", "vespa", "victory", "yamaha", "zero",

  // ── Models — trucks ──────────────────────────────────────────
  "bronco", "bronco-sport", "broncosport", "canyon", "colorado", "cybertruck",
  "expedition", "explorer", "f150", "f-150", "f250", "f-250", "f350", "f-350",
  "f450", "f-450", "frontier", "gladiator", "lightning", "maverick", "raptor",
  "ram1500", "ram2500", "ram3500", "ranger", "ridgeline", "r1t", "sierra",
  "silverado", "tacoma", "titan", "trx", "tundra",

  // ── Models — SUVs / crossovers ───────────────────────────────
  "4runner", "acadia", "armada", "ascent", "atlas", "bolt", "blazer",
  "cayenne", "ck1500", "compass", "crv", "cr-v", "crosstrek", "cx30",
  "cx-30", "cx5", "cx-5", "cx50", "cx-50", "cx9", "cx-9", "cx90", "cx-90",
  "defender", "discovery", "durango", "edge", "equinox", "escalade", "escape",
  "etron", "e-tron", "evoque", "expedition", "explorer", "forester",
  "g550", "g63", "g-wagon", "gwagon", "gla", "glb", "glc", "gle", "gls",
  "grand-cherokee", "grandcherokee", "grand-wagoneer", "grandwagoneer",
  "highlander", "hrv", "hr-v", "id4", "id-4", "ix3", "kona", "land-cruiser",
  "landcruiser", "lyriq", "macan", "mdx", "model-x", "modelx", "model-y",
  "modely", "murano", "outback", "palisade", "panamera", "passport",
  "pathfinder", "pilot", "q3", "q4", "q5", "q7", "q8", "qx50", "qx55",
  "qx60", "qx80", "range-rover", "rangerover", "rav4", "rdx", "renegade",
  "r1s", "rogue", "santa-fe", "santafe", "sequoia", "seltos", "sorento",
  "sportage", "suburban", "taos", "telluride", "tahoe", "taycan", "terrain",
  "tiguan", "trailblazer", "trax", "tucson", "venza", "wagoneer", "wilderness",
  "wrangler", "x1", "x2", "x3", "x4", "x5", "x6", "x7", "xc40", "xc60", "xc90",
  "xt4", "xt5", "xt6", "yukon",

  // ── Models — sedans / sports ────────────────────────────────
  "911", "918", "959", "accord", "altima", "amg", "amg-gt", "amggt", "arteon",
  "avalon", "avenger", "boxster", "brz", "camaro", "camry", "carrera",
  "cayman", "c43", "c63", "challenger", "charger", "civic", "civic-si",
  "civicsi", "corolla", "cls", "ct4", "ct5", "ct6", "cts-v", "ctsv", "demon",
  "diablo", "elantra", "es350", "es-350", "fairlady", "focus", "fusion",
  "g70", "g80", "g90", "gallardo", "gr86", "gr-corolla", "grcorolla",
  "gr-yaris", "gryaris", "gt2", "gt2rs", "gt3", "gt-3", "gt3rs", "gt4",
  "gt4rs", "gtr", "gt-r", "gv60", "gv70", "gv80", "hellcat", "huracan",
  "i4", "i7", "i8", "ilx", "impreza", "integra", "ioniq", "ioniq5",
  "ioniq-5", "ioniq6", "ioniq-6", "is300", "is500", "isf", "is-f", "jetta",
  "k5", "kicks", "lc500", "legend", "lfa", "ls500", "m2", "m3", "m4", "m5",
  "m8", "m340i", "m440i", "m550i", "m850i", "malibu", "maxima", "mazda3",
  "mazda-3", "mazda6", "mazda-6", "miata", "model-3", "model3", "model-s",
  "models", "mustang", "mustanggt", "mustang-gt", "mx5", "mx-5", "nsx",
  "panamera", "passat", "prius", "q50", "q60", "rcf", "rc-f", "rs3", "rs4",
  "rs5", "rs6", "rs7", "rsx", "r8", "scatpack", "scat-pack", "sentra",
  "shelby", "skyline", "sl63", "sonata", "spider", "spyder", "sti", "stinger",
  "stingray", "supra", "s2000", "s660", "tlx", "trx-450", "ttrs", "tt-rs",
  "type-r", "typer", "type-s", "types", "veloster", "viper", "vt2", "wrx",
  "x3m", "x3-m", "x4m", "x4-m", "x5m", "x5-m", "x6m", "x6-m", "z06", "zr1",
  "z4", "zl1",

  // ── Motorcycle models ───────────────────────────────────────
  "africa-twin", "africatwin", "bonneville", "cbr", "cbr1000rr", "cbr600rr",
  "challenger", "chief", "chieftain", "concours", "daytona", "diavel", "drz",
  "drz400", "duke", "duke390", "duke-390", "duke690", "duke-690", "duke790",
  "duke-790", "duke890", "duke-890", "exc", "fatboy", "fat-boy", "fireblade",
  "ftr", "ftr1200", "ftr-1200", "goldwing", "gold-wing", "grom", "gsxr",
  "gsx-r", "gsxr1000", "gsx-r1000", "gsxr600", "gsx-r600", "gsxr750",
  "gsx-r750", "hayabusa", "hypermotard", "iron883", "iron-883", "k1600",
  "katana", "klr650", "klr-650", "livewire", "live-wire", "lowrider",
  "low-rider", "m1000rr", "monster", "monkey", "mt07", "mt-07", "mt09",
  "mt-09", "mt10", "mt-10", "multistrada", "navi", "ninja", "ninja400",
  "ninja-400", "ninja650", "ninja-650", "ninja1000", "ninja-1000", "panigale",
  "pan-america", "panamerica", "r1250gs", "r1250-gs", "r-nine-t", "rninet",
  "rebel", "roadglide", "road-glide", "roadking", "road-king", "roadmaster",
  "roadster", "rsv4", "rs660", "rs-660", "s1000r", "s1000rr", "s1000xr",
  "scout", "scrambler", "shadow", "shiver", "softail", "speedtriple",
  "speed-triple", "sportster", "streetfighter", "streetglide", "street-glide",
  "supersport", "tenere", "tenere700", "tenere-700", "tiger", "tiger800",
  "tiger-800", "tiger900", "tiger-900", "tiger1200", "tiger-1200", "tracer",
  "tracer9", "tracer-9", "trident", "tuono", "ultra", "v-strom", "vstrom",
  "vulcan", "yzf", "yzfr1", "yzf-r1", "z900", "z650", "z400", "zx10r",
  "zx-10r", "zx14r", "zx-14r", "zx6r", "zx-6r",

  // ── Powersport models ──────────────────────────────────────
  "ace", "general", "outlander", "outlaw", "ranger-xp", "ryker", "rzr",
  "rzrpro", "rzr-pro", "rzrxp", "rzr-xp", "scrambler", "sportsman", "spyder",
];

export const RESERVED_HANDLES: ReadonlySet<string> = new Set(RESERVED_LIST);

/** True when a handle is on the reserved list (case-insensitive). */
export function isReservedHandle(handle: string): boolean {
  return RESERVED_HANDLES.has(handle.toLowerCase());
}
